import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, KeyRound, Edit, Loader, Trash2, AlertTriangle, LogOut, Settings, Save } from 'lucide-react';

// Nezapomeňte do api souboru přidat funkci pro odhlášení (např. logoutUser)
import { getAccountData, deleteAccount, logoutUser, updateImprovingAgree } from './../../api/BackendApi';

const AccountPage = () => {
  const navigate = useNavigate();
  const [accountData, setAccountData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // States for the Delete Modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deletePassword, setDeletePassword] = useState(''); // Nový state pro heslo

  const [improvingAgree, setImprovingAgree] = useState(false); // Boolean x accountData
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');
  const [settingsError, setSettingsError] = useState('');

  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    setSettingsMessage('');
    setSettingsError('');

    try {
      const updatedData = await updateImprovingAgree(improvingAgree);

      setAccountData((prev) => ({
        ...prev,
        improvingAgree: updatedData.improvingAgree,
      }));

      setImprovingAgree(Boolean(updatedData.improvingAgree));
      setSettingsMessage('Nastavení bylo uloženo.');
    } catch (err) {
      console.error('Failed to save account settings:', err);
      setSettingsError(err.message || 'Nepodařilo se uložit nastavení.');
    } finally {
      setIsSavingSettings(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getAccountData();
        setAccountData(data);
        setImprovingAgree(Boolean(data.improvingAgree));
      } catch (err) {
        setError('Nepodařilo se načíst data o účtu.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleLogout = async () => {
    try {
      await logoutUser();
      window.location.href = '/'; 
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError('Pro smazání účtu musíte zadat své heslo.');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');
    try {
      await deleteAccount(deletePassword);
      window.location.href = '/'; 
    } catch (err) {
      console.error("Failed to delete account:", err);
      setDeleteError(err.message || 'Nepodařilo se zrušit účet. Zkontrolujte heslo a zkuste to znovu.');
      setIsDeleting(false);
    }
  };

  // Helper pro zavření modálu a vyčištění formuláře
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteError('');
    setDeletePassword('');
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader className="animate-spin text-blue-600" /></div>;
  if (error) return <div className="text-center py-12 text-red-600">{error}</div>;
  if (!accountData) return null;

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Můj účet</h1>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Settings className="mr-2 text-blue-600" size={20} />
            Nastavení účtu
          </h2>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={improvingAgree}
                onChange={(e) => {
                  setImprovingAgree(e.target.checked);
                  setSettingsMessage('');
                  setSettingsError('');
                }}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />

              <div>
                <p className="text-sm font-medium text-gray-900">
                  Souhlasím s využitím poskytnutých dat pro zlepšování výsledků v rámci mého uživatelského účtu.
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  Souhlas můžete kdykoliv změnit zde.
                </p>
              </div>
            </label>
          </div>

          {settingsMessage && (
            <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {settingsMessage}
            </div>
          )}

          {settingsError && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {settingsError}
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={handleSaveSettings}
            disabled={isSavingSettings || improvingAgree === Boolean(accountData.improvingAgree)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSavingSettings ? (
              <Loader size={16} className="animate-spin mr-2" />
            ) : (
              <Save size={16} className="mr-2" />
            )}
            {isSavingSettings ? 'Ukládání...' : 'Uložit nastavení'}
          </button>
        </div>
      </div>
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <User className="mr-2 text-blue-600" size={20} />
            Fakturační údaje
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Název společnosti / Jméno a příjmení</p>
              <p className="font-medium text-gray-900">{accountData.name || "Nevyplněno"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">IČO</p>
              <p className="font-medium text-gray-900">{accountData.ico || "Nevyplněno"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">DIČ</p>
              <p className="font-medium text-gray-900">{accountData.dic || "Nevyplněno"}</p>
            </div>
          </div>

          <h2 className="text-lg font-medium text-gray-800 my-3 mb-1 flex items-center">
            Fakturační adresa
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Ulice</p>
              <p className="font-medium text-gray-900">{accountData.street || "Nevyplněno"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Číslo popisné</p>
              <p className="font-medium text-gray-900">{accountData.houseNumber || "Nevyplněno"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Město</p>
              <p className="font-medium text-gray-900">{accountData.city || "Nevyplněno"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">PSČ</p>
              <p className="font-medium text-gray-900">{accountData.postalCode || "Nevyplněno"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Země</p>
              <p className="font-medium text-gray-900">{accountData.country || "Nevyplněno"}</p>
            </div>
          </div>
        </div>
        
        {/* Actions Bar */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex flex-wrap gap-4 items-center">
          <button 
            onClick={handleLogout}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
          >
            <LogOut size={16} className="mr-2" />
            Odhlásit se
          </button>

          <button 
            onClick={() => navigate('/account/edit')}
            className="flex items-center px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
          >
            <Edit size={16} className="mr-2" />
            Upravit údaje
          </button>
          
          <button 
            onClick={() => navigate('/account/change-password')}
            className="flex items-center px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
          >
            <KeyRound size={16} className="mr-2" />
            Změnit heslo
          </button>

          {/* Delete Account Button (pushed to the right) */}
          <button 
            onClick={() => setIsDeleteModalOpen(true)}
            className="flex items-center px-4 py-2 bg-red-50 text-red-600 border border-red-100 rounded-lg hover:bg-red-100 transition-colors font-medium text-sm ml-auto"
          >
            <Trash2 size={16} className="mr-2" />
            Zrušit účet
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Opravdu chcete zrušit účet?
              </h3>
              <p className="text-gray-500 mb-4">
                Tato akce je nevratná. Všechna vaše data, analýzy a zbývající kredity budou trvale odstraněny.
              </p>

              {/* Password Input for verification */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="delete-password">
                  Pro potvrzení zadejte své heslo:
                </label>
                <input
                  id="delete-password"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Vaše heslo"
                  disabled={isDeleting}
                />
              </div>

              {deleteError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  {deleteError}
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <button
                  onClick={closeDeleteModal}
                  disabled={isDeleting}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                >
                  Zpět
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting || !deletePassword.trim()}
                  className="flex items-center px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? (
                    <Loader size={16} className="animate-spin mr-2" />
                  ) : (
                    <Trash2 size={16} className="mr-2" />
                  )}
                  {isDeleting ? 'Odstraňování...' : 'Ano, zrušit účet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountPage;